import dayjs from 'dayjs';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import {
  BusinessRuleItemType,
  BusinessRuleSolutionEnum,
  NUMBER_STRING_HASH,
  STRING_ANY_HASH,
  STRING_BOOLEAN_HASH,
  STRING_STRING_HASH,
} from '../constants/globals';
import { ChiroUpJSON } from '../functions/ChiroUpJSON';
import { AppointmentInsuranceType } from './Appointment.type';
import { KnownClearingHouses } from './BillingCode.type';
import type { ClaimLineEventType } from './ClaimLineEvent.type';
import { ClaimResponseEraStatus } from './Era.type';
import { IntegrationTypeEnum } from './Integration.type';
import type { Patient, PatientBalanceInfo } from './Patient.type';
import { PatientPrimaryInsured, PrimaryInsuredTypes } from './PatientInsurance.type';
import { PatientInvoice } from './PatientInvoice.type';
import {
  isaRefundItem,
  PatientTransaction,
  TransactionItemSubtypeEnum,
  TransactionPatientSummaryType,
} from './PatientTransaction.type';
import { ClaimInfo } from './PatientVisit.type';
import { PayorLineItemType } from './Payor.type';
import type { UserSettingsBillingType } from './UserSettingsBilling.type';

dayjs.extend(dayOfYear);

export type AdjustmentActionType = {
  action: string;
  type: string;
  row: any;
  pk: string;
  parentPk?: string;
  manual?: boolean;
};

export type AdjustInvoiceType = {
  clinicId: number;
  id: number;
  actions: AdjustmentActionType[];
  sidecar?: { [key: string]: boolean };
};

export enum KnownSidecarEnum {
  Invoices = 'invoices',
  Transaction = 'transaction',
}

export type KnownSidecars = {
  [KnownSidecarEnum.Invoices]?: IntegrationInvoice[];
  [KnownSidecarEnum.Transaction]?: PatientTransaction;
};

export type BusinessRuleIssueResponse = {
  count: number;
  issues: BusinessRuleItemType[]; // These are things that _must_ be addressed.
  notes: BusinessRuleItemType[]; // These are warnings or suggestions, etc. Not errors.
  data?: any;
  name?: keyof typeof BusinessRuleSolutionEnum;
  invoice?: IntegrationInvoice;
};

/**
 * This allows fine granularity of whether an option appears in
 * the adjustment tool.
 */
export const SupportedInvoiceActions = {
  [['era-to-cre', 'era'].join('.')]: true,
  [['status-to-post', 'cre'].join('.')]: true,
  [['status-to-applied', 'cre'].join('.')]: true,
  [['apply', 'pli'].join('.')]: true,
  [['post', 'cre'].join('.')]: true,
  [['apply-adj', 'cre'].join('.')]: true,
  [['unlock', 'cre'].join('.')]: true,
  [['assign-policy', 'invoice'].join('.')]: true,
  [['recreate', 'pli'].join('.')]: true,
  [['remove-item', 'transaction'].join('.')]: true,
  [['delete', 'payment'].join('.')]: true,
} as STRING_BOOLEAN_HASH;

enum ClaimDelayReasonEnum {
  EligibilityUnknown = 1,
  Litigation = 2,
  AuthorizationDelays = 3,
  CertifyingProvider = 4,
  SupplyingBillingForms = 5,
  CustomApplianceDelivery = 6,
  ThirdPartyProcessing = 7,
  EligibilityDetermination = 8,
  OriginalClaimRejectedOrDenied = 9,
  AdministrationPriorApprovalProcess = 10,
  Other = 11,
  NaturalDisaster = 15,
}

const ClaimDelayReasonText: { [key: number]: string } = {
  1: 'Proof of Eligibility Unknown',
  2: 'Litigation',
  3: 'Authorization Delays',
  4: 'Delay in Certifying Provider',
  5: 'Delay in Supplying Billing Forms',
  6: 'Delay in Delivery of Custom Appliances',
  7: 'Third Party Processing Delay',
  8: 'Delay in Eligibility Determination',
  9: 'Original Claim Rejected or Denied Due to Reason Unrelated to the Billing Limitation Rules',
  10: 'Administration Delay in the Prior Approval Process',
  11: 'Other',
  15: 'Natural Disaster',
};

export const ClaimDelayReasonOptions = Object.keys(ClaimDelayReasonEnum)
  .filter(key => /^\d+$/.test(key))
  .map(key => ({
    text: ClaimDelayReasonText[Number(key)] || `-- woops ${key} --`,
    value: key,
  }))
  .sort((a, b) => (a.text > b.text ? 1 : -1));

enum InvoiceGenerationEnum {
  Original = 'Original',
  Replacement = 'Replacement',
  Void = 'Void',
}

/**
 * Data structure to validate inputs.
 */
export const InvoiceGenerationEdiXref: { [key: string]: string } = {
  [InvoiceGenerationEnum.Original]: '1',
  [InvoiceGenerationEnum.Replacement]: '7',
  [InvoiceGenerationEnum.Void]: '8',
  '1': InvoiceGenerationEnum.Original,
  '7': InvoiceGenerationEnum.Replacement,
  '8': InvoiceGenerationEnum.Void,
};

/**
 * At this point, this is _only_ to select what we'll send to the EDI
 * for the generation. So, the values are the EDI field values and not
 * the strings from the enum.
 */
export const InvoiceGenerationOptions = Object.keys(InvoiceGenerationEnum).map(key => ({
  value: InvoiceGenerationEdiXref[key],
  text: key,
}));

export type InvoiceStatusSummaryResponse = {
  status: InvoiceStatusEnum;
  count: number;
  hasClaimLineData?: boolean;
};

export enum InvoiceStatusEnum {
  // Hold - Claim is on hold and will not be processed until the hold is
  // removed. Stops the claim from being processed without manual intervention.
  Hold = 'Hold',
  // Waiting on Other - Claim can not be generated until the claim ahead
  // of it in the priority order has been denied or paid. This can not be
  // set by a user. The system auto-sets this for downstream payors.
  Waiting = 'Waiting on Other',
  // Pending - Information that is required for claim generation is missing.
  Pending = 'Pending',
  // Unsubmitted - Claim is ready to be downloaded or submitted (this an
  // auto-status, users can not change a claim status to this).
  Unsubmitted = 'Unsubmitted',
  // Queued - Claim has been queued for batch submission and will be
  // processed shortly. Prevents duplicate submissions while in queue.
  Queued = 'Queued',
  // Submitted -Claim has been submitted to the Payor for reimbursement.
  Submitted = 'Submitted',
  // Received - this means that the Payor has acknowledged that they have
  // received the claim.
  Received = 'Received',
  // Rejected - Claim has not been processed by the Payor due to errors or
  // invalid information discovered during the initial validation process.
  Rejected = 'Rejected',
  // Denied - Claim has undergone review and the Payor determined the claim
  // to be ineligible for reimbursement.
  Denied = 'Denied',
  // Partially Paid - Claim has been partially paid and the clinic is
  // awaiting another claim response.
  Partial = 'Partially Paid',
  // Paid - Claim has been processed and payment from Payor has been received.
  Paid = 'Paid',
  // This is a status just for patient invoices that denotes submitted but not paid
  Unpaid = 'Unpaid',
  // Schizophrenic status.
  Reprocessed = 'Reprocessed', // Allow for paid and denied to transition to this.
}

export const FrontEndSummaryStatuses = [
  InvoiceStatusEnum.Pending,
  InvoiceStatusEnum.Unsubmitted,
  InvoiceStatusEnum.Submitted,
  InvoiceStatusEnum.Rejected,
  InvoiceStatusEnum.Denied,
];

export const isAdjudicatedStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return (
    i.status === InvoiceStatusEnum.Denied ||
    i.status === InvoiceStatusEnum.Unpaid ||
    i.status === InvoiceStatusEnum.Paid
  );
};

/**
 * [2026-01-16 for 10040640129 by Brian] Removed status-based gating. When
 * the user explicitly requests 837 generation, honor that request. Invoice
 * status is advisory - users and BAs can already modify it via various paths.
 * The signature-change flow archives PLIs but keeps the invoice status
 * unchanged; blocking regeneration based on status created unnecessary
 * friction. The block was: Pending, Unsubmitted, Rejected only.
 */
export const isaValidBatchSetStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return true;
};

export const isaInvoiceAdjustableStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return (
    i.status === InvoiceStatusEnum.Denied ||
    i.status === InvoiceStatusEnum.Unpaid ||
    i.status === InvoiceStatusEnum.Paid ||
    i.status === InvoiceStatusEnum.Received ||
    i.status === InvoiceStatusEnum.Reprocessed ||
    i.status === InvoiceStatusEnum.Rejected
  );
};

// [2025-07-28.1207 by Brian via Emily] Changes to a claim (invoice) status
// should ONLY automatically un-finalize payor line items IF the claim status
// of the finalized (last) ERA is moved to Reprocessed OR Rejected.
export const isaClearFinalizedStatus = (i: IntegrationInvoice) => {
  if (!i || !i.status) return false;

  return ClearFinalizedStatusHash[i.status as keyof typeof ClearFinalizedStatusHash];
};

export const ClearFinalizedStatusHash = {
  [InvoiceStatusEnum.Reprocessed]: true,
  [InvoiceStatusEnum.Rejected]: true,
};

export const SupportedFinalizedStatuses = {
  [InvoiceStatusEnum.Paid]: true,
  [InvoiceStatusEnum.Unpaid]: true,
  [InvoiceStatusEnum.Denied]: true,
};

export const isaBrokenInvoice = (invoice: IntegrationInvoice) => {
  return (invoice?.priority ?? 0) <= 0 && canReverseSubmit(invoice);
};

export const canReverseSubmit = (invoice: IntegrationInvoice) => {
  return (
    invoice?.status === InvoiceStatusEnum.Submitted ||
    invoice?.status === InvoiceStatusEnum.Reprocessed ||
    invoice?.status === InvoiceStatusEnum.Received
  );
};

/**
 * Fix 7 (BA): Users must be able to generate/regenerate a claim when the invoice is
 * Unsubmitted/Rejected (and downstream-ready Pending), regardless of whether prior adjudication
 * exists elsewhere on the purchase (applied/posted).
 *
 * NOTE: This is strictly a "can we submit now?" gate. It intentionally does NOT attempt to
 * encode adjudication rules; those are handled elsewhere (e.g., edit protections).
 */
export const isSubmittableClaim = (invoice: IntegrationInvoice) => {
  return (
    invoice?.status === InvoiceStatusEnum.Unsubmitted ||
    invoice?.status === InvoiceStatusEnum.Rejected ||
    invoice?.status === InvoiceStatusEnum.Pending ||
    invoice?.status === InvoiceStatusEnum.Queued
  );
};

export const InvoiceSubmittedStatusList = [
  InvoiceStatusEnum.Submitted,
  InvoiceStatusEnum.Received,
  InvoiceStatusEnum.Partial,
];

export const isStatusWaitingSupported = (status: InvoiceStatusEnum) => {
  switch (status) {
    case InvoiceStatusEnum.Pending:
    case InvoiceStatusEnum.Unsubmitted:
      return true;
    default:
      return false;
  }
};

export const InvoiceStatusTips: STRING_STRING_HASH = {
  [InvoiceStatusEnum.Hold]: 'All claim processing is disabled until the hold is removed.',
  [InvoiceStatusEnum.Waiting]: [
    'Claim can not be generated until the claim ahead',
    'of it in the priority order has been denied or paid.',
  ].join(' '),
  [InvoiceStatusEnum.Pending]: 'Information that is required for claim generation is missing.',
  [InvoiceStatusEnum.Queued]: 'Claim is queued for batch submission.',
  [InvoiceStatusEnum.Unsubmitted]: 'Claim is ready to be submitted.',
  [InvoiceStatusEnum.Submitted]: 'Claim has been submitted to the Payor for reimbursement.',
  [InvoiceStatusEnum.Received]: 'Indicates that the payor has acknowledged receipt of the claim.',
  [InvoiceStatusEnum.Rejected]: [
    'Payor has not processed the claim due to errors or',
    'invalid information discovered during the validation process.',
  ].join(' '),
  // Denied - Claim has undergone review and the Payor determined the claim
  // to be ineligible for reimbursement.
  [InvoiceStatusEnum.Denied]: [
    'Payor has reviewed the claim and determined it',
    'to be ineligible for reimbursement',
  ].join(' '),
  [InvoiceStatusEnum.Partial]: [
    'Claim has been partially paid and the clinic is',
    'awaiting another claim response.',
  ].join(' '),
  [InvoiceStatusEnum.Paid]: 'Claim has been processed and payment has been received.',
  [InvoiceStatusEnum.Reprocessed]: 'Claim has been reprocessed.',
};

/**
 * The trick here is that the indices of the values and the
 * keys are in the same order so the options can be created
 * correctly with the ones a user can't select filtered out.
 */
let vals: any[] = Object.values(InvoiceStatusEnum).filter(
  v => v !== 'Unsubmitted' && v !== 'Waiting on Other' && v !== 'Queued'
);

const AllInvoiceStatusDisplay: STRING_STRING_HASH = {};
vals = Object.values(InvoiceStatusEnum);
const overRide: STRING_STRING_HASH = {
  [InvoiceStatusEnum.Waiting]: 'Waiting',
  [InvoiceStatusEnum.Partial]: 'Partial',
};
export const AllInvoiceStatusOptions = Object.keys(InvoiceStatusEnum).map((key, idx) => {
  AllInvoiceStatusDisplay[vals[idx]] = key;

  return { text: overRide[vals[idx]] ?? vals[idx], value: key };
});

export const AllowedInvoiceStatusTransitions: STRING_ANY_HASH = {
  [InvoiceStatusEnum.Submitted]: {
    [InvoiceStatusEnum.Received]: true,
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Unsubmitted]: true,
  },
  [InvoiceStatusEnum.Received]: {
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Paid]: true,
  },
  [InvoiceStatusEnum.Rejected]: {
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Denied]: true,
    [InvoiceStatusEnum.Submitted]: true,
  },
  [InvoiceStatusEnum.Denied]: {
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Unpaid]: true,
    [InvoiceStatusEnum.Reprocessed]: true,
  },
  [InvoiceStatusEnum.Partial]: {},
  [InvoiceStatusEnum.Paid]: {
    [InvoiceStatusEnum.Reprocessed]: true,
    [InvoiceStatusEnum.Rejected]: true,
    [InvoiceStatusEnum.Denied]: true,
  },
  [InvoiceStatusEnum.Unpaid]: {
    [InvoiceStatusEnum.Reprocessed]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
  [InvoiceStatusEnum.Pending]: {
    [InvoiceStatusEnum.Hold]: true,
  },
  [InvoiceStatusEnum.Unsubmitted]: {
    [InvoiceStatusEnum.Hold]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
  [InvoiceStatusEnum.Queued]: {
    [InvoiceStatusEnum.Submitted]: true,
    [InvoiceStatusEnum.Pending]: true,
    [InvoiceStatusEnum.Unsubmitted]: true,
  },
  [InvoiceStatusEnum.Waiting]: {
    [InvoiceStatusEnum.Pending]: true,
    [InvoiceStatusEnum.Hold]: true,
  },
  [InvoiceStatusEnum.Hold]: {
    [InvoiceStatusEnum.Pending]: true,
  },
  [InvoiceStatusEnum.Reprocessed]: {
    [InvoiceStatusEnum.Paid]: true,
    [InvoiceStatusEnum.Rejected]: true,
  },
};

/**
 * 277 processing policy
 *
 * Rationale:
 * - Submitted/Received/Reprocessed: pre-adjudicative states; a 277 rejection should move the claim to rejected.
 * - Pending/Unsubmitted/Waiting: still allow rejection and notify via activity (unexpected but valid).
 * - Hold: administrative hold; do not move off hold automatically. Attach issues and record activity.
 * - Paid/Partial/Unpaid/Denied: adjudicated outcomes; do not unwind automatically. Attach issues and record activity.
 */
export type Invoice277PolicyEntry = {
  allow: boolean;
  changeStatus: InvoiceStatusEnum | null;
  warn: boolean; // When true, also record a transaction activity explaining the exception semantics
};

export const Invoice277ProcessingPolicy: Record<InvoiceStatusEnum, Invoice277PolicyEntry> = {
  [InvoiceStatusEnum.Submitted]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Received]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Reprocessed]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Rejected]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },

  [InvoiceStatusEnum.Pending]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Unsubmitted]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Queued]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },
  [InvoiceStatusEnum.Waiting]: {
    allow: true,
    changeStatus: InvoiceStatusEnum.Rejected,
    warn: true,
  },

  // Administrative hold should not be auto-cleared
  [InvoiceStatusEnum.Hold]: { allow: true, changeStatus: null, warn: true },

  // Adjudicated outcomes should not be unwound automatically
  [InvoiceStatusEnum.Paid]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Partial]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Unpaid]: { allow: true, changeStatus: null, warn: true },
  [InvoiceStatusEnum.Denied]: { allow: true, changeStatus: null, warn: true },
};

export type InvoiceInsert = {
  balance?: number;
  billedAmount?: number;
  billingKey: string;
  clinicId: number;
  createdAt?: number;
  estimated?: number;
  estimatedBalance?: number;
  insuranceId?: string;
  invoiceData?: any;
  issuedDate?: number;
  patientId: string;
  patientResponsibility?: number;
  payorId?: number;
  status: string;
  writeOffTotal?: number;
  finalized?: boolean;
  rev?: number;
};

export type InvoiceEdiMetaType = {
  delayReason?: string;
  generation?: string;
  claimControlNumber?: string;
};
export interface FinalizeItem {
  id: number;
  code: string;
  finalized: boolean;
  claimResponseEraId: number;
  claimStatus: ClaimResponseEraStatus;
  eraId: number;
  clinicId: number;
  lineItemControlNumber?: string;
  billingKey?: string;
}

/**
 * Response body shared by the per-line toggle branches of
 * `integrationInvoiceStatusUpdate` (`finalize-purchase-items`,
 * `dispute-purchase-items`, `undispute-purchase-items`).
 */
export type ServiceLineToggleResponse = {
  message: string;
  lineItemIds: number[];
  updatedItems: FinalizeItem[];
  claimLineEvents?: ClaimLineEventType[];
  payorLineItems?: PayorLineItemType[];
  patientInvoice?: IntegrationInvoice;
  patientBalance?: PatientBalanceInfo;
};

export type Invoice = InvoiceInsert & {
  // isBillingStarted is NOT ALWAYS FILLED IN! Make sure the query
  // you're using is pulling it if you need it!!!
  //The claim control number is provided in the claim response from the payor...
  asciiPayorId?: string;
  billingCodes?: string[];
  claimControlNumber?: string;
  controlNumber?: number;
  currentControlNumber?: string;
  delayReason?: string;
  deleted?: boolean;
  dispatched?: boolean; //printed or emailed invoice
  eraIds?: string[];
  exchangeId?: string;
  generation?: string;
  id: number;
  interchangeControlNumber?: number;
  invoiceData?: any | PatientInvoice;
  isBillingStarted?: boolean; // Calculated from PayorLineItem.
  issuedDate?: number;
  legalName?: string;
  number: string;
  priority?: number;
  priorityLast?: number;
  statusAt?: number;
  statusData?: InvoiceStatusData | null;
  superBill?: boolean;
  tz?: string; // Timezone: Pulled from ClinicLocation, not in the Invoice table.
  uiLegalName?: string;
  // Optional count of insurance-billable service items on the purchase
  insuranceBillableItemCount?: number;
  // UI-specific metadata in some circumstances.
  _meta?: {
    userSettingsBilling?: UserSettingsBillingType;
    patientRecord?: Patient;
  };
};

export type SuperbillDisplay = {
  total: number;
  totalPaid?: number;
  onlyBillable: boolean;
};

export type ExtendedGetInvoiceResponse = GetInvoiceResponse & {
  _superbill?: SuperbillDisplay;
};

export enum GetIntegrationInvoiceUsage {
  EraSmartPosting = 'era-smart-posting',
}

/**
 * Integration invoices have a lot of extra details than normal invoices.
 * To retrieve them, joins to multiple tables are necessary so use the
 * normal invoice most of the time. Use this type of invoice when specific
 * details about whether an EDI document needs to be created. The extra
 * fields are needed for running the business rules: UI and back end.
 *
 * Most of the time EDI creation is not an issue when dealing with invoices
 * so the extra table joins are not needed.
 */
export type IntegrationInvoice = Invoice & {
  billingProfile?: IntegrationInvoiceBillingProfile;
  billingProfileId?: number;
  billingProfileName?: string;
  billingProfileType?: string;
  // Optional count of insurance-billable service items (for UI gating like superbill button)
  insuranceBillableItemCount?: number;
  claimInfo?: ClaimInfo;
  electronicBilling: boolean;
  hasClaimResponse?: boolean;
  integrationActive?: boolean;
  integrationId?: number;
  integrationType?: IntegrationTypeEnum;
  interchangeControlNumber?: number;
  isFlagged?: boolean;
  legalName: string;
  noteCount?: number;
  patient: TransactionPatientSummaryType;
  payorLineItems?: PayorLineItemType[];
  payorLineItemsOther?: PayorLineItemType[];
  claimLineEvents?: ClaimLineEventType[];
  payorFax?: string;
  pos?: string;
  primaryInsured?: PatientPrimaryInsured & {
    memberId: string;
    type: PrimaryInsuredTypes;
  };
  provider?: IntegrationInvoiceProvider;
  providerDisplayName: string;
  touched?: number;
  transactionDate?: number;
  transactionItems?: uiPayorInvoiceLineItem[];
  clearinghouse?: KnownClearingHouses;
  insurances?: AppointmentInsuranceType[];
  /** Policy (PatientInsurance) active flag; when false, policy was marked inactive. */
  policyActive?: boolean;
  /** When policy was marked inactive (set when active goes 0). Used for alert copy. */
  policyInactivatedAt?: number | null;
  /** Patient policy type (primary/secondary/tertiary/other) for billing order mismatch alert. */
  policyType?: string;
  /** Tracks the status before an invoice was moved to Queued, so it can be reverted on failure. */
  _previousStatus?: InvoiceStatusEnum;
  /** Billing order for this insurance on the appointment (from AppointmentInsurance). Null = patient invoice. */
  billingPriority?: number | null;
};

/**
 * Invoice shape returned by the `transactionList` endpoint.
 * Core fields from Invoice + computed SQL columns.
 */
export type TransactionListInvoice = Pick<
  Invoice,
  'billedAmount' | 'balance' | 'status' | 'id' | 'billingKey'
> & {
  displayName: string;
  paid: number;
};

export type IntegrationInvoiceBillingProfile = {
  id: number;
  name: string;
  type: string;
  npi: string;
  taxId: string;
};

export type IntegrationInvoiceProvider = {
  name: string;
  npi: string;
  taxonomyCode: string;
};

export type IntegrationInvoiceProcessedType = {
  [key: number]: {
    issues: BusinessRuleItemType[];
    notes: BusinessRuleItemType[];
    success: boolean;
  };
};

/**
 * Return shape of createBatchSet(). Used by batch837Drain
 * to avoid `any` on the result. Fields beyond meta/processed are optional
 * because early-return paths omit them.
 */
export type CreateBatchSetResult = {
  meta?: {
    count?: number;
    issues?: BusinessRuleItemType[];
  };
  processed?: IntegrationInvoiceProcessedType;
  exchangeId?: string;
  [key: string]: unknown;
};

export type IntegrationInvoicePayorSummary = {
  payorId: number;
  billedAmount: number;
  balance: number;
};

export type IntegrationInvoiceStatusUpdateResponse = {
  invoices: IntegrationInvoice[];
  issues: BusinessRuleItemType[];
  notes: BusinessRuleItemType[];
};

/**
 * A trivial little helper to generate a baseId for an invoice. The
 * 'id' and 'baseId' are added together to get the invoiceId that is
 * sent to the payor in EDI.
 *
 * This _may_ be unnecessary, but it seemed weird to have invoice nos
 * of 1, 2, 3,... So, we go off the base of the current year minus 2020
 * followed by the day of the year and then the id of the invoice. This
 * should always be unique but harder to guess across clinics.
 *
 * @returns
 */
export const invoiceBaseId = () => {
  const yearPart = Number(dayjs().format('YYYY')) - 2020,
    dayPart = `000${dayjs().dayOfYear()}`.slice(-3);

  return Number(`${yearPart}${dayPart}`);
};

/**
 * Yes, I know, this goes to ELEVEN!!!  Wonder if the EDI
 * boffins did that on purpose!
 */
enum PayorPriorityEnum {
  Primary = 'P',
  Secondary = 'S',
  Tertiary = 'T',
  Fourth = 'A',
  Fifth = 'B',
  Sixth = 'C',
  Seventh = 'D',
  Eighth = 'E',
  Nineth = 'F',
  Tenth = 'G',
  Eleventh = 'H',
}

export const PayorPriorityDisplay: NUMBER_STRING_HASH = {
  [-1]: '-na-',
  0: '-na-',
  1: 'Primary',
  2: 'Secondary',
  3: 'Tertiary',
  4: 'Fourth',
  5: 'Fifth',
  6: 'Sixth',
  7: 'Seventh',
  8: 'Eighth',
  9: 'Nineth',
  10: 'Tenth',
  11: 'Eleventh',
};

vals = Object.values(PayorPriorityEnum);
const PayorPriorityOptions = Object.keys(PayorPriorityEnum).map((key, idx) => ({
  value: vals[idx],
  text: key,
}));

export const PayorPriorityIdText: NUMBER_STRING_HASH = PayorPriorityOptions.reduce((a, c, i) => {
  a[i + 1] = c.text;

  return a;
}, {} as NUMBER_STRING_HASH);

type respType = {
  serviceTotal: number;
  involvesSpinalManipulation: boolean;
  patientAmountPaid: number;
};

/**
 * This returns details about the transaction/purchase that are needed for
 * an invoice and for EDI.
 *
 *
 * @param param0
 * @returns
 */
export const invoiceTransactionMetaData = ({
  transaction,
}: {
  transaction: PatientTransaction;
}): respType => {
  let resp: respType = {
    serviceTotal: 0,
    involvesSpinalManipulation: false,
    patientAmountPaid: 0,
  };

  if (!transaction || !transaction.items || !transaction.items.length) {
    return resp;
  }

  resp = transaction.items.reduce(
    (acc, item) => {
      if (item.subtype === TransactionItemSubtypeEnum.Service) {
        acc.serviceTotal += Number(item.amount) * Number(item.units);

        if (
          item.code === '98940' ||
          item.code === '98941' ||
          item.code === '98942' ||
          item.code === '98940S'
        ) {
          acc.involvesSpinalManipulation = true;
        }
      } else if (item.subtype === TransactionItemSubtypeEnum.PatientPayment) {
        acc.patientAmountPaid += Number(item.amount);
      } else if (item.subtype === TransactionItemSubtypeEnum.PatientCredit) {
        acc.patientAmountPaid += Number(item.amount);
      } else if (isaRefundItem(item)) {
        acc.patientAmountPaid -= Number(item.amount);
      }

      return acc;
    },
    resp // Reduce initial value from default response.
  );

  return resp;
};

type _ediItemFields = {
  qualifier: string;
  code: string;
  name: string;
};

// Partial claim line selection by PTI id, keyed by invoice (single-invoice only).
export type ServicePtiIdSelection = { [invoiceId: number]: number[] };

export type CreateEdiPostContextType = {
  billingKey?: string;
  invoiceIds?: number[];
  invoice?: Invoice;
  payorPriorityCode?: string;
  meta?: {
    [key: number]: {
      delayReason?: string;
      generation?: string;
      claimControlNumber?: string;
    };
  }; // Additional details to be sent to the EDI.
  targetStatus?: string;
  debug?: boolean;
  trace?: boolean;
  update?: boolean;
  allOrNone?: boolean;
  batch?: boolean;
  mock?: {
    enable: boolean;
    values: { [key: string]: any };
  };
  testUpload?: boolean;
  /** Force paper claim processing (fetch all fields needed for CMS1500) regardless of invoice.electronicBilling */
  forcePaperClaim?: boolean;
  createClaimLineEvents?: boolean; // ISL 'gate' flag
  servicePtiIds?: ServicePtiIdSelection; // partial claim line selection by PTI id (single-invoice only)
};

export type CreateEdiPostType = {
  context: CreateEdiPostContextType;
};

export type InvoiceStatusData = {
  items: InvoiceStatusDataItemType[];
};

/**
 * Create an empty invoice status data object. This returns a new
 * object, so you don't need to worry about mutating the original object.
 *
 * @returns An empty invoice status data object.
 */
export const emptyInvoiceStatusData = (): InvoiceStatusData => {
  return { items: [] } as InvoiceStatusData;
};

/**
 * Source tracking enum for invoice status data items.
 * Each value is spaced by 20 to allow for future insertions.
 * The value indicates where the status data item was created.
 *
 * NEVER CHANGE THESE OR REUSE A NUMBER!!!!! Just add new ones.
 * We always need to be able to decipher the chain of events and
 * modifying these will break that.
 */
export enum InvoiceStatusDataSource {
  IntegrationInvoiceStatusUpdate = 20,
  CreateBatchSetTest = 40,
  CreateBatchSetSubmit = 60,
  CreateBatchSetPartialSubmit = 70,
  SetInvoiceStatus = 80,
  ParseResponseFile_277 = 100,
  ParseResponseFile_835 = 120,
  ParseResponseFile_999 = 140,
  UpdateInvoiceStatusFromClaimResponse_Processed = 160,
  UpdateInvoiceStatusFromClaimResponse_DeniedRejected = 180,
  UpdateInvoiceStatusFromClaimResponse_Received = 200,
  UpdateInvoiceStatusFromClaimResponse_ForwardedTransferred = 220,
  UpdateInvoiceStatusFromClaimResponse_ForwardedTransferred_NextInvoice = 240,
  UpdateInvoiceStatusFromClaimResponse_Default = 260,
  IntegrationInvoiceAdjust = 280,
  ClaimResponseEra_PostPayment = 300,
  ClaimResponseEra_ClearUnposted = 320,
  ClaimResponseEra_ReapplyAdjudication = 340,
  ClaimResponseEra_Reprocess = 360,
  TransactionClaimAssociation = 380,
  DevtoolsInvoicePanelMove = 400,
  NextInvoiceByPriority = 420,
  PayorPaymentUnapply = 440,
  UnpostInvoice = 460,
  BusinessRules_ResolveInvoiceIssues = 480,
  BusinessRules_PrimaryInvoiceWaiting = 500,
  SetInvoiceValues = 520,
  PatientBillingInvoiceHelpers = 540,
  InitialInvoiceStatusData = 560,
  SignatureChangeArchive = 580,
  EraApply = 600,
  TransactionSave = 620,
  AccumulatePayorLineItems_EnsurePlis = 640,
  BatchQueue837 = 660,
  BatchQueue837Failed = 680,
  NotificationOnlyRejected = 700,
  BillerFinalize = 720,
  BillerFinalizeAutoRevoke = 740,
  BillerUnfinalize = 760,
  EraClaimResponseDrain_Adjudicated = 780,
  EraClaimResponseDrain_Crossover835 = 800,
  UpdateInvoiceStatusFromClaimResponse_ISL_PerLine = 820,
  BillerDispute = 840,
  BillerUnpostRevert = 860,
  ClaimResponseEra_RepostAdjudicated = 880,
  BillerUndispute = 900,
}

/** Queue-specific issue text for non-cause invoices in a failed chunk. */
export const BATCH_QUEUE_COLLATERAL_FAILURE_TEXT =
  'Another invoice in this chunk failed validation.';

export type InvoiceStatusDataItemType = {
  claimControlNumber?: string;
  delayReason?: string;
  exchangeId?: string;
  final: string;
  generation?: string;
  interchangeControlNumber?: string;
  issues: BusinessRuleItemType[];
  notes: BusinessRuleItemType[];
  payorLineItems?: PayorLineItemType[];
  previous: string;
  target: string;
  ts: string;
  userId: string;
  userName?: string;
  electronicBilling?: boolean;
  src: InvoiceStatusDataSource;
};

const initialInvoiceStatusData = ({ userId, invoice }: { userId: string; invoice?: Invoice }) => {
  return {
    items: [
      {
        ts: Date.now().toString(),
        userId,
        previous: '-none-',
        target: InvoiceStatusEnum.Pending,
        final: invoice?.status ?? InvoiceStatusEnum.Pending,
        issues: [],
        notes: [],
        src: InvoiceStatusDataSource.InitialInvoiceStatusData,
      },
    ],
  };
};

export type GetInvoiceNotesResponse = {
  rows: GetInvoiceNotesRow[];
};

export type GetInvoiceNotesRow = {
  id: number;
  note: string;
  createdAt: string;
  userId: string;
  createdBy: string;
};

// This IS NOT an integration invoice.
export type GetInvoiceResponse = Invoice & {
  locationId: number;
  patientTransactionId: number;
  transactionDate: string;
};

export type uiPayorInvoiceLineItem = {
  id: number;
  code: string;
  diags?: string[];
  modifier1?: string;
  modifier2?: string;
  modifier3?: string;
  modifier4?: string;
  units: number;
  amount: number;
  lineItemControlNumber?: string | null; // NULL until the line's first 837 submission
  isDrugCode?: boolean;
  nationalDrugCode?: string;
  ndcUnits?: number;
  ndcUnitType?: string;
};

/**
 * This adds a new item to the invoice status data. It will create an
 * initial empty one if it does not exist. Otherwise, it just addes to
 * the list.
 *
 * Note that the MOST RECENT IS AT THE TOP. That is items[0] is always
 * the latest.
 *
 * @param param0
 * @returns
 */
export const addInvoiceStatusDataItem = ({
  invoice,
  issues = [],
  notes = [],
  payorLineItems,
  revertStatus,
  targetStatus,
  userId,
  userName,
  src,
}: {
  invoice?: Invoice | null;
  issues?: BusinessRuleItemType[];
  notes?: BusinessRuleItemType[];
  payorLineItems?: PayorLineItemType[];
  revertStatus?: string;
  targetStatus: string;
  userId: string;
  userName?: string;
  src: InvoiceStatusDataSource;
}) => {
  if (!invoice) throw new Error('Invoice is required.');
  if (!userId) throw new Error('userId is required.');

  let current = null;

  current = invoice.statusData ? invoice.statusData : initialInvoiceStatusData({ userId, invoice });

  if (typeof current === 'string') {
    current = JSON.parse(current) as InvoiceStatusData;
  }

  revertStatus = revertStatus || invoice.status;

  if (
    issues &&
    issues.length > 0 &&
    (targetStatus === InvoiceStatusEnum.Received || targetStatus === InvoiceStatusEnum.Reprocessed)
  ) {
    revertStatus = InvoiceStatusEnum.Rejected;
  }

  /**
   * Rule: It makes no sense for a primary to be "waiting" on
   * anything if there are no issues. So, it is at least pending.
   */
  if (invoice.status === InvoiceStatusEnum.Waiting && invoice.priority === 1) {
    revertStatus = InvoiceStatusEnum.Pending;
  }

  /**
   * Helper to sanitize null/undefined values from being saved as strings.
   * Only include the field if it has a real value.
   */
  const sanitizeValue = (value: any): string | undefined => {
    if (value === null || value === undefined) return undefined;
    const stringValue = String(value);

    // Parse as number - if NaN or 0, reject it
    const parsed = parseInt(stringValue, 10);

    if (isNaN(parsed) || parsed === 0) {
      return undefined;
    }

    return stringValue;
  };

  const item: Partial<InvoiceStatusDataItemType> = {
    ts: Date.now().toString(),
    userId,
    previous: invoice.status,
    target: targetStatus,
    final: issues && Array.isArray(issues) && issues.length === 0 ? targetStatus : revertStatus,
    issues,
    notes,
    src,
  };

  // Only add optional fields if they have real values
  const claimControlNumber = sanitizeValue(invoice.claimControlNumber);
  if (claimControlNumber) item.claimControlNumber = claimControlNumber;

  const exchangeId = sanitizeValue(invoice.exchangeId);
  if (exchangeId) item.exchangeId = exchangeId;

  const interchangeControlNumber = sanitizeValue(invoice.interchangeControlNumber);
  if (interchangeControlNumber) item.interchangeControlNumber = interchangeControlNumber;

  const delayReason = sanitizeValue(invoice.delayReason);
  if (delayReason) item.delayReason = delayReason;

  const generation = sanitizeValue(invoice.generation);
  if (generation) item.generation = generation;

  if (userName) item.userName = userName;

  // Only add payorLineItems if provided, not empty, and not already in a previous status item
  if (payorLineItems && payorLineItems.length > 0) {
    // Create a content-based fingerprint to detect duplicates (not just IDs - PLIs can be updated)
    const sortedPlis = [...payorLineItems].sort((a, b) => Number(a.id) - Number(b.id));
    const newPliFingerprint = ChiroUpJSON.stringify(sortedPlis);

    // Check if this exact PLI content already exists in a previous status item
    const existingItems = (current as InvoiceStatusData)?.items || [];
    const isDuplicate = existingItems.some(existingItem => {
      if (!existingItem.payorLineItems || existingItem.payorLineItems.length === 0) {
        return false;
      }

      const sortedExisting = [...existingItem.payorLineItems].sort(
        (a, b) => Number(a.id) - Number(b.id)
      );
      const existingFingerprint = ChiroUpJSON.stringify(sortedExisting);

      return existingFingerprint === newPliFingerprint;
    });

    if (!isDuplicate) {
      item.payorLineItems = ChiroUpJSON.noNulls(payorLineItems) as PayorLineItemType[];
    }
  }

  const clone = ChiroUpJSON.clone(current, emptyInvoiceStatusData());
  clone.items = clone.items || [];
  clone.items.unshift(item as InvoiceStatusDataItemType);

  return clone as InvoiceStatusData;
};

// Helper for invoice timestamp updates - stores UTC epoch milliseconds
// Note: InvoiceTouchedJoin is kept for backward compatibility with existing queries
// that may reference it, even though it's no longer needed for timestamp calculation.
export const InvoiceTouchedJoin = `
  LEFT JOIN PatientTransaction pt ON pt.billingKey = i.billingKey AND pt.clinicId = i.clinicId
  LEFT JOIN ClinicLocations cl ON cl.id = pt.locationId
`;

// [2025-01-10] Fixed: Previously used timezone conversion which produced incorrect
// epoch values. UNIX_TIMESTAMP(NOW()) correctly returns UTC epoch milliseconds.
// Timezone conversion should only happen at DISPLAY time, not STORAGE time.
export const InvoiceTouchedSql = `UNIX_TIMESTAMP(NOW()) * 1000`;

/**
 * [2025-07-02.1532 by Brian] Updated this to a helper function where we could
 * pass some parameters to. For now, we jsut needed some stuff just before the
 * where clause. Keeping it DRY.
 *
 * Hold my nose and type. This is in one place. It needed to be shared between
 * CUP and admin app, and this makes as much sense as anything.
 *
 * Update the status, timestamps, billedAmount and balance. If there
 * are no line items, the amounts do not change. So, if they are null,
 * they stay null. [2024-05-24.1509 by Brian]
 *
 * [2025-01-10] Simplified: tz parameter is now ignored. Timestamps are stored
 * as UTC epoch milliseconds using UNIX_TIMESTAMP(NOW()). No JOIN needed.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const getSqlUpdateInvoiceStatus = (beforeWhere?: string, tz?: string | null) => {
  // Normalize: ensure beforeWhere starts with a comma if provided
  let normalizedBeforeWhere = beforeWhere?.trim() || '';

  if (normalizedBeforeWhere && !normalizedBeforeWhere.startsWith(',')) {
    normalizedBeforeWhere = ', ' + normalizedBeforeWhere;
  }

  return `
  UPDATE Invoice i
     SET i.status = :status
       , i.statusData = :statusData
       , i.statusAt = ${InvoiceTouchedSql}
       , i.touched = ${InvoiceTouchedSql}
       , i.issuedDate = IF(:status = '${InvoiceStatusEnum.Submitted}', COALESCE(i.issuedDate, ${InvoiceTouchedSql}), i.issuedDate)
       , i.billedAmount = COALESCE((SELECT SUM(pli.billedAmount) FROM PayorLineItem pli WHERE pli.clinicId = i.clinicId AND pli.billingKey = i.billingKey AND pli.invoiceId = i.id), i.billedAmount)
       , i.balance = COALESCE((SELECT SUM(pli.billedAmount - pli.remitAmount - pli.adjustmentTotal) FROM PayorLineItem pli WHERE pli.clinicId = i.clinicId AND pli.billingKey = i.billingKey AND pli.invoiceId = i.id), i.balance)
    ${normalizedBeforeWhere}
  WHERE i.id = :id
    AND i.clinicId = :clinicId`;
};

// DRY - [2025-01-10] Removed JOIN since InvoiceTouchedSql no longer needs cl.timezone
export const UPDATE_INVOICE_DATA_QUERY = `
  UPDATE Invoice i
     SET i.invoiceData = :invoiceData
       , i.touched = ${InvoiceTouchedSql}
     WHERE i.id = :id
       AND i.clinicId = :clinicId`;

/**
 * Attachment types for PWK01 segment (Report Type Code)
 * Used to identify the type of clinical documentation attached to a claim
 *
 * Re-exported from server-core/constants/claimAttachmentTypes for consistency
 */
export { CLAIM_ATTACHMENT_TYPES_OPTIONS as AttachmentTypeOptions } from '@chiroup/server-core/constants/claimAttachmentTypes';

// Legacy enum - kept for backwards compatibility but prefer using string values from CLAIM_ATTACHMENT_TYPES
export enum AttachmentTypeEnum {
  Chiropractic = 'CB',
  Diagnostic = 'DG',
  Support = 'OZ',
}

/**
 * Transmission methods for PWK02 segment (Transmission Code)
 * Defines how the attachment will be transmitted to the payor
 */
export enum TransmissionMethodEnum {
  AvailableOnRequest = 'AA', // Available on Request at Provider Site
  ByMail = 'BM', // By Mail
  Electronically = 'EL', // Electronically Only (default)
  Email = 'EM', // Email
  FileTransfer = 'FT', // File Transfer
  Fax = 'FX', // By Fax
}

export const TransmissionMethodOptions = [
  {
    value: TransmissionMethodEnum.AvailableOnRequest,
    text: 'AA - Available on Request at Provider Site',
  },
  { value: TransmissionMethodEnum.ByMail, text: 'BM - By Mail' },
  { value: TransmissionMethodEnum.Electronically, text: 'EL - Electronically Only' },
  { value: TransmissionMethodEnum.Email, text: 'EM - Email' },
  { value: TransmissionMethodEnum.FileTransfer, text: 'FT - File Transfer' },
  { value: TransmissionMethodEnum.Fax, text: 'FX - By Fax' },
];

/**
 * Invoice Attachment type
 * Supporting clinical documentation attached to a payor invoice for claim submission
 */
export type InvoiceAttachment = {
  id: number;
  invoiceId: number;
  clinicId: number;
  attachmentType: AttachmentTypeEnum;
  transmissionMethod: TransmissionMethodEnum;
  attachmentName: string;
  createdAt: number;
  createdBy?: string;
  createdByName?: string;
  deletedAt?: number;
  deletedBy?: string;
};

export const MAX_INVOICE_ATTACHMENTS = 10;

/**
 * Statuses where attachments can be added/modified
 * Only certain claim statuses allow modifications to attachments
 */
export const AttachmentEditableStatuses = [
  InvoiceStatusEnum.Hold,
  InvoiceStatusEnum.Waiting,
  InvoiceStatusEnum.Pending,
  InvoiceStatusEnum.Unsubmitted,
  InvoiceStatusEnum.Rejected,
];
